// Enterprise Observability Panel — Fase 14.7
// Activated by: ?obs=1 | localStorage['canteiro:obs']='1' | Ctrl+Shift+O
import React, { useState, useEffect, useCallback } from 'react'
import { getLastHealthSnapshot }  from '@/core/runtimeHealthMonitor'
import { getChannelSnapshot }     from '@/core/leak/channelMonitor'
import { getLastLeakReport }      from '@/core/leak/memoryLeakDetector'
import { getRegistrySnapshot }    from '@/core/leak/intervalRegistry'
import { runIntegrityCheck }      from '@/core/storeIntegrityValidator'
import { getSafetySnapshot }      from '@/core/safety/operationalSafetyLayer'
import { getOrchestratorHealth }  from '@/core/orchestration/runtimeOrchestrator'
import { runtimeTopologyReport }  from '@/core/governance/runtimeTopologyReport'
import { useTVHealthStore }       from '@/store/tvHealthStore'

function shouldShow(): boolean {
  if (typeof window === 'undefined') return false
  if (new URLSearchParams(window.location.search).get('obs') === '1') return true
  try { return localStorage.getItem('canteiro:obs') === '1' } catch { return false }
}

function rc(val: number, warn: number, crit: number): string {
  return val >= crit ? '#ff4444' : val >= warn ? '#ffaa00' : '#44ff88'
}
function sc(s: string): string {
  return s === 'ok' || s === 'healthy' || s === 'closed' || s === 'running' ? '#44ff88' :
         s === 'degraded' || s === 'warning' || s === 'half-open' ? '#ffaa00' : '#ff4444'
}

// --- Snapshot type — tudo coletado fora do render ---
interface ObsSnap {
  health:      ReturnType<typeof getLastHealthSnapshot>
  chanSnap:    ReturnType<typeof getChannelSnapshot>
  leakReport:  ReturnType<typeof getLastLeakReport>
  registry:    ReturnType<typeof getRegistrySnapshot>
  integrity:   ReturnType<typeof runIntegrityCheck>
  safety:      ReturnType<typeof getSafetySnapshot>
  orchHealth:  ReturnType<typeof getOrchestratorHealth>
  topology:    ReturnType<typeof runtimeTopologyReport>
  fps:         number
}

function collectObs(): ObsSnap {
  return {
    health:     getLastHealthSnapshot(),
    chanSnap:   getChannelSnapshot(),
    leakReport: getLastLeakReport(),
    registry:   getRegistrySnapshot(),
    integrity:  runIntegrityCheck(),
    safety:     getSafetySnapshot(),
    orchHealth: getOrchestratorHealth(),
    topology:   runtimeTopologyReport(),
    fps:        useTVHealthStore.getState().fps,
  }
}

export function EnterpriseObservabilityPanel(): React.ReactElement | null {
  const [visible, setVisible] = useState(shouldShow)
  const [snap,    setSnap]    = useState<ObsSnap | null>(null)
  const [ts,      setTs]      = useState('')

  const refresh = useCallback(() => {
    setTimeout(() => {
      try {
        setSnap(collectObs())
        setTs(new Date().toLocaleTimeString())
      } catch { /* ignore */ }
    }, 0)
  }, [])

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'O') {
        setVisible((v) => {
          const n = !v
          try { localStorage.setItem('canteiro:obs', n ? '1' : '0') } catch { /* ignore */ }
          return n
        })
      }
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [])

  useEffect(() => {
    if (!visible) return
    refresh()
    const t = setInterval(refresh, 5_000)
    return () => clearInterval(t)
  }, [visible, refresh])

  function handleClose() {
    setVisible(false)
    try { localStorage.setItem('canteiro:obs', '0') } catch { /* ignore */ }
  }

  if (!visible || !snap) return null

  const { health, chanSnap, leakReport, registry, integrity, safety, orchHealth, topology, fps } = snap
  const intervals = registry.filter((r) => r.type === 'interval').length
  const timeouts  = registry.filter((r) => r.type === 'timeout').length
  const rafs      = registry.filter((r) => r.type === 'raf').length

  const panel: React.CSSProperties = { position: 'fixed', bottom: 8, right: 8, width: 320, maxHeight: '90vh', overflowY: 'auto', background: 'rgba(0,0,0,0.92)', color: '#e0e0e0', fontFamily: 'monospace', fontSize: 11, padding: 12, borderRadius: 6, border: '1px solid #333', zIndex: 99999 }
  const row: React.CSSProperties  = { display: 'flex', justifyContent: 'space-between', marginBottom: 2 }
  const sec: React.CSSProperties  = { marginTop: 8, borderTop: '1px solid #333', paddingTop: 6 }
  const lbl: React.CSSProperties  = { color: '#888' }
  const ttl: React.CSSProperties  = { color: '#aaa', fontWeight: 'bold', marginBottom: 4 }

  return (
    <div style={panel}>
      <div style={ttl}>
        obs panel
        <span style={{ float: 'right', cursor: 'pointer', color: '#666', marginLeft: 8 }} onClick={handleClose}>X</span>
        <span style={{ float: 'right', cursor: 'pointer', color: '#555' }} onClick={refresh}>r</span>
      </div>
      <div style={sec}><div style={ttl}>Engines</div>
        <div style={row}><span style={lbl}>overall</span><span style={{ color: sc(orchHealth.overall) }}>{orchHealth.overall}</span></div>
        <div style={row}><span style={lbl}>run/fail</span><span>{orchHealth.running} / <span style={{ color: orchHealth.failed > 0 ? '#ff4444' : '#44ff88' }}>{orchHealth.failed}</span></span></div>
        <div style={row}><span style={lbl}>health</span><span style={{ color: sc(health?.overall ?? 'no data') }}>{health?.overall ?? 'no data'}</span></div>
      </div>
      <div style={sec}><div style={ttl}>Memory</div>
        <div style={row}><span style={lbl}>heap</span><span style={{ color: rc(health?.memory.heapUsedMB ?? 0, 400, 700) }}>{health?.memory.heapUsedMB?.toFixed(0) ?? '--'} MB</span></div>
        <div style={row}><span style={lbl}>leak risk</span><span>{leakReport?.overallRisk ?? 'no data'}</span></div>
      </div>
      <div style={sec}><div style={ttl}>TV</div>
        <div style={row}><span style={lbl}>fps</span><span style={{ color: rc(fps, 30, 20) }}>{fps > 0 ? fps.toFixed(0) : '--'}</span></div>
      </div>
      <div style={sec}><div style={ttl}>Channels</div>
        <div style={row}><span style={lbl}>open</span><span>{chanSnap.openCount}</span></div>
        <div style={row}><span style={lbl}>zombies</span><span style={{ color: chanSnap.zombies.length > 0 ? '#ffaa00' : '#44ff88' }}>{chanSnap.zombies.length}</span></div>
        <div style={row}><span style={lbl}>rt status</span><span style={{ color: sc(health?.realtime.status ?? 'offline') }}>{health?.realtime.status ?? 'no data'}</span></div>
      </div>
      <div style={sec}><div style={ttl}>Resources</div>
        <div style={row}><span style={lbl}>intervals</span><span style={{ color: rc(intervals, 20, 50) }}>{intervals}</span></div>
        <div style={row}><span style={lbl}>timeouts</span><span>{timeouts}</span></div>
        <div style={row}><span style={lbl}>rafs</span><span>{rafs}</span></div>
      </div>
      <div style={sec}><div style={ttl}>Integrity</div>
        <div style={row}><span style={lbl}>status</span><span style={{ color: sc(integrity.ok ? 'ok' : 'warning') }}>{integrity.ok ? 'ok' : `${integrity.violations.length} violação(ões)`}</span></div>
        {integrity.violations.slice(0,2).map((v, i) => <div key={i} style={{ color: '#ffaa00', fontSize: 10 }}>! {v.store}: {v.message}</div>)}
      </div>
      <div style={sec}><div style={ttl}>Safety</div>
        <div style={row}><span style={lbl}>safe mode</span><span style={{ color: safety.safeMode ? '#ffaa00' : '#44ff88' }}>{safety.safeMode ? 'ACTIVE' : 'off'}</span></div>
        <div style={row}><span style={lbl}>degraded</span><span style={{ color: safety.degraded.active ? '#ff4444' : '#44ff88' }}>{safety.degraded.active ? (safety.degraded.reason ?? 'yes') : 'no'}</span></div>
        {safety.circuits.filter((c) => c.state !== 'closed').map((c) => <div key={c.module} style={{ color: '#ffaa00', fontSize: 10 }}>! {c.module}: {c.state}</div>)}
      </div>
      <div style={sec}><div style={{ fontSize: 10, color: '#777' }}>{topology.summary}</div>
        {topology.potentialOrphans.length > 0 && <div style={{ color: '#ffaa00', fontSize: 10 }}>! {topology.potentialOrphans.length} orphan(s)</div>}
      </div>
      <div style={{ marginTop: 8, fontSize: 9, color: '#444' }}>{ts} · Ctrl+Shift+O</div>
    </div>
  )
}
